import type { StarterTemplate } from "../types";

type Spec = Record<string, unknown>;

const COLORS = {
  navy: "#172b4d",
  blue: "#2563eb",
  sky: "#e9f2ff",
  mint: "#dcfce7",
  amber: "#fef3c7",
  rose: "#ffe4e6",
  violet: "#ede9fe",
  white: "#ffffff",
  slate: "#64748b",
};

function node(
  id: string,
  type: "rectangle" | "ellipse" | "diamond",
  x: number,
  y: number,
  width: number,
  height: number,
  text: string,
  backgroundColor = COLORS.white,
  strokeColor = COLORS.navy,
): Spec {
  return {
    id,
    type,
    x,
    y,
    width,
    height,
    backgroundColor,
    strokeColor,
    fillStyle: "solid",
    strokeWidth: 2,
    roundness: type === "rectangle" ? { type: 3 } : undefined,
    label: { text, fontSize: 18, strokeColor },
  };
}

function arrow(id: string, x: number, y: number, dx: number, dy: number, strokeColor = COLORS.slate): Spec {
  return {
    id,
    type: "arrow",
    x,
    y,
    points: [
      [0, 0],
      [dx, dy],
    ],
    strokeColor,
    strokeWidth: 2,
    endArrowhead: "arrow",
  };
}

function heading(id: string, x: number, y: number, text: string, color = COLORS.navy): Spec {
  return { id, type: "text", x, y, text, fontSize: 28, strokeColor: color, fontFamily: 2 };
}

export const STARTER_TEMPLATES: readonly StarterTemplate[] = [
  {
    id: "tripwire-funnel",
    name: "Tripwire Funnel",
    description: "Trafik kepada tawaran kecil, upsell dan produk utama.",
    accent: "#2563eb",
    icon: "funnel",
    specs: [
      heading("tf-h", 80, 60, "Tripwire Funnel"),
      node("tf-1", "rectangle", 80, 160, 170, 92, "Trafik\nBerbayar", COLORS.sky),
      arrow("tf-a1", 250, 206, 90, 0),
      node("tf-2", "rectangle", 340, 150, 200, 112, "Landing Page\nLead Magnet", COLORS.white),
      arrow("tf-a2", 540, 206, 90, 0),
      node("tf-3", "rectangle", 630, 150, 200, 112, "Tripwire\nRM7 – RM47", COLORS.amber),
      arrow("tf-a3", 830, 206, 90, 0),
      node("tf-4", "rectangle", 920, 150, 210, 112, "Upsell\nTawaran Utama", COLORS.mint),
      arrow("tf-a4", 1_025, 262, 0, 95),
      node("tf-5", "rectangle", 915, 357, 220, 100, "Susulan E-mel /\nWhatsApp", COLORS.violet),
    ],
  },
  {
    id: "lead-magnet-funnel",
    name: "Lead Magnet Funnel",
    description: "Tukar trafik kepada prospek dan siri pemupukan.",
    accent: "#0891b2",
    icon: "magnet",
    specs: [
      heading("lm-h", 80, 60, "Lead Magnet Funnel"),
      node("lm-1", "ellipse", 80, 155, 150, 110, "Trafik", COLORS.sky),
      arrow("lm-a1", 230, 210, 90, 0),
      node("lm-2", "rectangle", 320, 150, 210, 120, "Halaman Opt-in\n+ Lead Magnet"),
      arrow("lm-a2", 530, 210, 90, 0),
      node("lm-3", "rectangle", 620, 150, 210, 120, "Halaman\nTerima Kasih", COLORS.mint),
      arrow("lm-a3", 830, 210, 90, 0),
      node("lm-4", "rectangle", 920, 150, 210, 120, "Siri E-mel\n5–7 Hari", COLORS.violet),
      arrow("lm-a4", 1_025, 270, 0, 95),
      node("lm-5", "diamond", 940, 365, 170, 130, "Berminat?", COLORS.amber),
      arrow("lm-a5", 940, 430, -140, 0),
      node("lm-6", "rectangle", 590, 380, 210, 100, "Tawaran Utama", COLORS.rose),
    ],
  },
  {
    id: "webinar-funnel",
    name: "Webinar Funnel",
    description: "Aliran pendaftaran, peringatan, webinar dan jualan.",
    accent: "#7c3aed",
    icon: "webinar",
    specs: [
      heading("wf-h", 80, 60, "Webinar Funnel"),
      node("wf-1", "rectangle", 80, 160, 180, 100, "Iklan /\nKandungan", COLORS.sky),
      arrow("wf-a1", 260, 210, 80, 0),
      node("wf-2", "rectangle", 340, 150, 210, 120, "Halaman\nPendaftaran"),
      arrow("wf-a2", 550, 210, 80, 0),
      node("wf-3", "rectangle", 630, 150, 210, 120, "Peringatan\nE-mel + WhatsApp", COLORS.amber),
      arrow("wf-a3", 840, 210, 80, 0),
      node("wf-4", "ellipse", 920, 145, 210, 130, "Webinar\nLangsung", COLORS.violet),
      arrow("wf-a4", 1_025, 275, 0, 95),
      node("wf-5", "diamond", 935, 370, 180, 140, "Hadir?", COLORS.white),
      arrow("wf-a5", 935, 440, -150, 0),
      node("wf-6", "rectangle", 570, 390, 215, 105, "Tawaran +\nDeadline", COLORS.rose),
      arrow("wf-a6", 1_115, 440, 145, 0),
      node("wf-7", "rectangle", 1_260, 390, 215, 105, "Replay +\nSusulan", COLORS.mint),
    ],
  },
  {
    id: "whatsapp-closing-funnel",
    name: "WhatsApp Closing Funnel",
    description: "Daripada iklan klik-ke-WhatsApp kepada closing dan susulan.",
    accent: "#16a34a",
    icon: "whatsapp",
    specs: [
      heading("wa-h", 80, 60, "WhatsApp Closing Funnel"),
      node("wa-1", "rectangle", 80, 155, 190, 110, "Iklan Klik ke\nWhatsApp", COLORS.mint),
      arrow("wa-a1", 270, 210, 80, 0),
      node("wa-2", "rectangle", 350, 155, 190, 110, "Soalan\nKelayakan"),
      arrow("wa-a2", 540, 210, 80, 0),
      node("wa-3", "diamond", 620, 145, 180, 130, "Layak?", COLORS.amber),
      arrow("wa-a3", 800, 210, 90, 0),
      node("wa-4", "rectangle", 890, 155, 210, 110, "Skrip Konsultasi\n+ Bukti", COLORS.sky),
      arrow("wa-a4", 1_100, 210, 90, 0),
      node("wa-5", "rectangle", 1_190, 155, 190, 110, "Pembayaran", COLORS.rose),
      arrow("wa-a5", 710, 275, 0, 115),
      node("wa-6", "rectangle", 600, 390, 220, 100, "Susulan Automatik\n1 / 3 / 7 Hari", COLORS.violet),
    ],
  },
  {
    id: "saas-registration-flow",
    name: "SaaS Registration Flow",
    description: "Aliran daftar, sah e-mel, onboarding dan aktifkan akaun.",
    accent: "#0f766e",
    icon: "saas",
    specs: [
      heading("sf-h", 80, 60, "SaaS Registration Flow"),
      node("sf-1", "rectangle", 80, 155, 190, 110, "Landing Page", COLORS.sky),
      arrow("sf-a1", 270, 210, 80, 0),
      node("sf-2", "rectangle", 350, 155, 190, 110, "Daftar Akaun"),
      arrow("sf-a2", 540, 210, 80, 0),
      node("sf-3", "diamond", 620, 145, 180, 130, "Data Sah?", COLORS.amber),
      arrow("sf-a3", 800, 210, 80, 0),
      node("sf-4", "rectangle", 880, 155, 210, 110, "Sahkan E-mel", COLORS.violet),
      arrow("sf-a4", 1_090, 210, 80, 0),
      node("sf-5", "rectangle", 1_170, 155, 210, 110, "Onboarding", COLORS.mint),
      arrow("sf-a5", 1_275, 265, 0, 105),
      node("sf-6", "ellipse", 1_170, 370, 210, 120, "Akaun Aktif", COLORS.sky),
      arrow("sf-a6", 710, 275, 0, 105),
      node("sf-7", "rectangle", 600, 380, 220, 100, "Papar Ralat +\nCuba Semula", COLORS.rose),
    ],
  },
  {
    id: "cold-warm-hot-journey",
    name: "Cold → Warm → Hot Customer Journey",
    description: "Petakan perjalanan kesedaran hingga pembelian dan advokasi.",
    accent: "#ea580c",
    icon: "journey",
    specs: [
      heading("cj-h", 80, 60, "Cold → Warm → Hot Customer Journey"),
      node("cj-1", "diamond", 90, 155, 170, 130, "COLD", COLORS.sky),
      arrow("cj-a1", 260, 220, 90, 0),
      node("cj-2", "rectangle", 350, 160, 210, 120, "Kesedaran\nIklan + Kandungan"),
      arrow("cj-a2", 560, 220, 90, 0),
      node("cj-3", "diamond", 650, 155, 170, 130, "WARM", COLORS.amber),
      arrow("cj-a3", 820, 220, 90, 0),
      node("cj-4", "rectangle", 910, 160, 210, 120, "Pertimbangan\nBukti + Nurture", COLORS.violet),
      arrow("cj-a4", 1_120, 220, 90, 0),
      node("cj-5", "diamond", 1_210, 155, 170, 130, "HOT", COLORS.rose),
      arrow("cj-a5", 1_295, 285, 0, 95),
      node("cj-6", "rectangle", 1_185, 380, 220, 105, "Pembelian", COLORS.mint),
      arrow("cj-a6", 1_185, 432, -120, 0),
      node("cj-7", "ellipse", 855, 375, 210, 115, "Advokasi +\nRepeat Order", COLORS.sky),
    ],
  },
];

export function getTemplate(id: string): StarterTemplate | undefined {
  return STARTER_TEMPLATES.find((template) => template.id === id);
}

